import React, { useState, useEffect, useCallback } from 'react';
import { Users, UserPlus, Mail, ShieldCheck, ShieldOff, UserX } from 'lucide-react';
import { householdAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { ConfirmModal, AddMemberModal } from '../modals';
import SearchInput from '../common/SearchInput';
import FilterDropdown from '../common/FilterDropdown';
import { Tooltip } from '../common';

/**
 * HouseholdMembers - Members management tab
 */
function HouseholdMembers({ isManager }) {
  const { success, error: showError } = useToast();
  const [members, setMembers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const loadMembers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await householdAPI.getMembers();
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to load members:', error);
      showError('Failed to load household members');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const loadPendingRequests = useCallback(async () => {
    try {
      const response = await householdAPI.getJoinRequests();
      setPendingRequests(response.data);
    } catch (error) {
      console.error('Failed to load pending requests:', error);
    }
  }, []);

  useEffect(() => {
    loadMembers();
    if (isManager) loadPendingRequests();
  }, [isManager, loadMembers, loadPendingRequests]);

  const handleApproveRequest = async (requestId, name) => {
    setProcessing(requestId);
    try {
      await householdAPI.processJoinRequest(requestId, 'approve');
      success(`${name} has been added to your household`);
      setPendingRequests(pendingRequests.filter(r => r.id !== requestId));
      loadMembers();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to approve request');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectRequest = async (requestId) => {
    setProcessing(requestId);
    try {
      await householdAPI.processJoinRequest(requestId, 'reject');
      success('Join request rejected');
      setPendingRequests(pendingRequests.filter(r => r.id !== requestId));
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to reject request');
    } finally {
      setProcessing(null);
    }
  };

  const getPrimaryMemberId = () => {
    if (members.length === 0) return null;
    const sortedByDate = [...members].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    return sortedByDate[0]?.id;
  };

  const handleToggleRole = async (member) => {
    const primaryMemberId = getPrimaryMemberId();
    if (primaryMemberId === member.id) {
      showError('The first member of a household must always be a manager');
      return;
    }

    try {
      const newRole = member.household_role === 'manager' ? 'member' : 'manager';
      await householdAPI.updateMemberRole(member.id, newRole);
      success(`${member.first_name} ${member.last_name} ${newRole === 'manager' ? 'promoted to' : 'demoted to'} ${newRole}`);
      loadMembers();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to update member role');
    }
  };

  const handleRemove = (member) => {
    setSelectedMember(member);
    setShowRemoveModal(true);
  };

  const confirmRemove = async () => {
    if (!selectedMember) return;

    const primaryMemberId = getPrimaryMemberId();
    if (primaryMemberId === selectedMember.id) {
      showError('The first member of a household cannot be removed');
      setShowRemoveModal(false);
      setSelectedMember(null);
      return;
    }

    try {
      await householdAPI.removeMember(selectedMember.id);
      success(`${selectedMember.first_name} ${selectedMember.last_name} removed from household`);
      setShowRemoveModal(false);
      setSelectedMember(null);
      loadMembers();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to remove member from household');
    }
  };

  // Filter members
  const filteredMembers = members.filter(member => {
    const matchesSearch = 
      member.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || member.household_role === roleFilter;
    const isInvited = !member.is_active && member.preferences?.invited_by;
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && member.is_active) ||
      (statusFilter === 'invited' && isInvited) ||
      (statusFilter === 'inactive' && !member.is_active && !isInvited);
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const primaryMemberId = getPrimaryMemberId();

  // Filter config for dropdown
  const filterConfig = [
    {
      id: 'role',
      label: 'Role',
      type: 'select',
      value: roleFilter,
      onChange: setRoleFilter,
      options: [
        { value: 'all', label: 'All Roles' },
        { value: 'manager', label: 'Managers' },
        { value: 'member', label: 'Members' }
      ]
    },
    {
      id: 'status',
      label: 'Status',
      type: 'select',
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'active', label: 'Active' },
        { value: 'invited', label: 'Invited' },
        { value: 'inactive', label: 'Inactive' }
      ]
    }
  ];

  const activeFilterCount = [roleFilter, statusFilter].filter(f => f !== 'all').length;

  if (loading) {
    return (
      <div className="household-section">
        <h2>Household Members</h2>
        <div className="loading-state">Loading members...</div>
      </div>
    );
  }

  return (
    <div className="household-section">
      <div className="section-header">
        <div>
          <h2>Household Members</h2>
          <p className="section-description">
            {isManager ? 'Manage household members and their roles' : 'View household members'}
          </p>
        </div>
        {isManager && (
          <div className="header-actions">
            <button className="btn btn-primary btn-icon-mobile" onClick={() => setShowAddMemberModal(true)} title="Add Member">
              <UserPlus size={18} />
              <span className="btn-text">Add Member</span>
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search members..."
        />
        <FilterDropdown
          filters={filterConfig}
          activeCount={activeFilterCount}
        />
      </div>

      {/* Pending Requests */}
      {isManager && pendingRequests.length > 0 && (
        <div className="pending-requests-section">
          <h3 className="subsection-title">Pending Requests ({pendingRequests.length})</h3>
          <div className="members-list">
            {pendingRequests.map(request => (
              <div key={`pending-${request.id}`} className="member-card pending">
                <div className="member-card-main">
                  <div className="user-avatar pending">
                    {request.first_name[0]}{request.last_name[0]}
                  </div>
                  <div className="member-info">
                    <h4>{request.first_name} {request.last_name}</h4>
                    <span className="member-email">{request.email}</span>
                  </div>
                  <span className="role-badge pending">Pending</span>
                </div>
                <div className="member-card-actions">
                  <button
                    className="member-action-btn success"
                    onClick={() => handleApproveRequest(request.id, `${request.first_name} ${request.last_name}`)}
                    disabled={processing === request.id}
                  >
                    Approve
                  </button>
                  <button
                    className="member-action-btn danger"
                    onClick={() => handleRejectRequest(request.id)}
                    disabled={processing === request.id}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Members List */}
      {filteredMembers.length === 0 ? (
        <div className="empty-state">
          <Users size={48} />
          <h3>No members found</h3>
          <p>Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="members-list">
          {filteredMembers.map(member => {
            const isPrimary = member.id === primaryMemberId;
            const isInvited = !member.is_active && member.preferences?.invited_by;
            const invitedBy = member.preferences?.invited_by;
            
            const getStatusBadge = () => {
              if (member.is_active) {
                return <span className="status-badge active">✓ Active</span>;
              }
              if (isInvited) {
                const tooltipContent = invitedBy ? (
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>Invited by {invitedBy.name}</div>
                    {invitedBy.invitedAt && (
                      <div style={{ fontSize: '12px', opacity: 0.8 }}>
                        {new Date(invitedBy.invitedAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ) : null;
                
                return (
                  <Tooltip content={tooltipContent} position="top">
                    <span className="status-badge invited">
                      <Mail size={12} style={{ marginRight: '4px' }} />
                      Invited
                    </span>
                  </Tooltip>
                );
              }
              return <span className="status-badge inactive">✕ Inactive</span>;
            };
            
            return (
              <div key={member.id} className={`member-card ${!member.is_active ? 'inactive' : ''}`}>
                <div className="member-card-main">
                  <div className="user-avatar">
                    {member.first_name[0]}{member.last_name[0]}
                  </div>
                  <div className="member-info">
                    <h4>{member.first_name} {member.last_name}</h4>
                    <span className="member-email">{member.email}</span>
                  </div>
                  <div className="member-badges">
                    <span className={`role-badge ${member.household_role}`}>
                      {member.household_role === 'manager' ? '👑 Manager' : '👤 Member'}
                    </span>
                    {getStatusBadge()}
                  </div>
                </div>
                {isManager && (
                  <div className="member-card-actions">
                    {!isPrimary && member.household_role !== 'manager' && (
                      <button className="member-action-btn" onClick={() => handleToggleRole(member)} title="Promote to Manager">
                        <ShieldCheck size={14} />
                        <span>Promote</span>
                      </button>
                    )}
                    {!isPrimary && member.household_role === 'manager' && (
                      <button className="member-action-btn demote" onClick={() => handleToggleRole(member)} title="Demote to Member">
                        <ShieldOff size={14} />
                        <span>Demote</span>
                      </button>
                    )}
                    {!isPrimary && (
                      <button className="member-action-btn danger" onClick={() => handleRemove(member)} title="Remove from Household">
                        <UserX size={14} />
                        <span>Remove</span>
                      </button>
                    )}
                    {isPrimary && (
                      <span className="primary-member-label">Primary Member</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="list-footer">
        Showing {filteredMembers.length} of {members.length} member{members.length !== 1 ? 's' : ''}
      </div>

      <AddMemberModal
        isOpen={showAddMemberModal}
        onClose={() => setShowAddMemberModal(false)}
        onAdd={async (userId, role) => {
          await householdAPI.inviteUser(userId, role);
          success('Invitation sent successfully');
          setShowAddMemberModal(false);
          loadMembers();
        }}
        onInviteSent={(email) => {
          success(`Invitation email sent to ${email}`);
          loadMembers();
          setShowAddMemberModal(false);
        }}
      />

      <ConfirmModal
        isOpen={showRemoveModal}
        onClose={() => { setShowRemoveModal(false); setSelectedMember(null); }}
        onConfirm={confirmRemove}
        title="Remove Member"
        message={`Are you sure you want to remove ${selectedMember?.first_name} ${selectedMember?.last_name} from your household?`}
        confirmText="Remove Member"
        variant="danger"
      />
    </div>
  );
}

export default HouseholdMembers;
